import { useEffect, useState } from "react";
import { api } from "@/lib/apiClient";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { PageHeader, formatDate } from "@/components/shared";
import { useAuth } from "@/contexts/AuthContext";
import { UserPlus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

const empty = { email: "", password: "", name: "", role: "staff", park_ids: [] };

export default function UsersPage() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [parks, setParks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(empty);

  const load = async () => {
    setLoading(true);
    try {
      const [u, p] = await Promise.all([api.get("/users"), api.get("/parks")]);
      setUsers(u.data); setParks(p.data);
    } catch { toast.error("Failed to load users"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const parkName = (id) => parks.find((p) => p.id === id)?.name || id;

  const openNew = () => { setEditingId(null); setForm(empty); setOpen(true); };
  const openEdit = (u) => { setEditingId(u.id); setForm({ email: u.email, password: "", name: u.name, role: u.role, park_ids: u.park_ids || [] }); setOpen(true); };

  const toggleParkAssignment = (pid) => {
    setForm((f) => ({
      ...f,
      park_ids: f.park_ids.includes(pid) ? f.park_ids.filter((x) => x !== pid) : [...f.park_ids, pid],
    }));
  };

  const save = async () => {
    if (form.role !== "super_admin" && form.park_ids.length === 0) {
      toast.error("park_admin and staff users must be assigned to at least one park");
      return;
    }
    try {
      if (editingId) {
        const payload = { name: form.name, role: form.role, park_ids: form.park_ids };
        if (form.password) payload.password = form.password;
        await api.put(`/users/${editingId}`, payload);
        toast.success("User updated");
      } else {
        if (!form.email || !form.password || !form.name) { toast.error("Email, name and password required"); return; }
        await api.post("/users", form);
        toast.success("User created");
      }
      setOpen(false);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const remove = async (u) => {
    if (u.id === me?.id) { toast.error("You can't delete yourself"); return; }
    if (!window.confirm(`Delete user ${u.email}?`)) return;
    try { await api.delete(`/users/${u.id}`); toast.success("Deleted"); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div data-testid="users-page">
      <PageHeader
        title="Users"
        subtitle="Manage staff, park admins, and super admins"
        actions={<Button onClick={openNew} className="bg-[#284B3B] hover:bg-[#1E3F32] text-white" data-testid="new-user-button"><UserPlus size={14} className="mr-2" /> New User</Button>}
      />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="!bg-white border-[#EAEAE5] max-w-xl">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "Outfit" }}>{editingId ? "Edit User" : "New User"}</DialogTitle>
            <DialogDescription className="text-xs text-[#737A76]">Super admins see all parks. Park admins and staff are assigned to specific parks.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs uppercase tracking-wider text-[#737A76]">Email</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} disabled={!!editingId} data-testid="user-email-input" /></div>
              <div><Label className="text-xs uppercase tracking-wider text-[#737A76]">Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-name-input" /></div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-[#737A76]">Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="staff">Staff</SelectItem>
                  <SelectItem value="park_admin">Park Admin</SelectItem>
                  <SelectItem value="super_admin">Super Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.role !== "super_admin" && (
              <div>
                <Label className="text-xs uppercase tracking-wider text-[#737A76]">Parks ({form.park_ids.length} assigned)</Label>
                <div className="mt-2 grid grid-cols-2 gap-2 max-h-40 overflow-y-auto p-3 border border-[#EAEAE5] rounded-lg bg-[#FAFAF7]" data-testid="user-park-picker">
                  {parks.map((p) => {
                    const sel = form.park_ids.includes(p.id);
                    return (
                      <button
                        key={p.id} type="button" onClick={() => toggleParkAssignment(p.id)}
                        data-testid={`user-park-${p.id}`}
                        className={`p-2 rounded-md text-xs border text-left transition ${sel ? "bg-[#284B3B] text-white border-[#284B3B]" : "bg-white border-[#EAEAE5] hover:border-[#284B3B]"}`}
                      >
                        {p.name}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            <div><Label className="text-xs uppercase tracking-wider text-[#737A76]">{editingId ? "New password (leave blank to keep)" : "Password"}</Label><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="user-password-input" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="bg-[#284B3B] hover:bg-[#1E3F32] text-white" onClick={save} data-testid="save-user-button">{editingId ? "Save" : "Create"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Card className="border-[#EAEAE5] shadow-none overflow-hidden">
        <CardContent className="p-0">
          <table className="w-full text-sm pd-table">
            <thead><tr className="text-left border-b border-[#EAEAE5] bg-[#FAFAF7]">
              <th className="px-4 py-3">Name</th><th>Email</th><th>Role</th><th>Parks</th><th>Created</th><th></th>
            </tr></thead>
            <tbody>
              {loading ? <tr><td colSpan={6} className="px-4 py-8 text-center text-[#737A76]">Loading…</td></tr>
              : users.map((u) => (
                <tr key={u.id} className="border-b border-[#F0F0EC] last:border-0" data-testid={`user-row-${u.id}`}>
                  <td className="px-4 py-3 font-medium">{u.name}</td>
                  <td className="text-[#737A76]">{u.email}</td>
                  <td><span className={`text-[11px] px-2 py-0.5 rounded-full ${u.role === "super_admin" ? "bg-[#F0F0EC] text-[#284B3B]" : u.role === "park_admin" ? "bg-[#FFF4D4] text-[#946A00]" : "bg-[#FAFAF7] text-[#737A76]"}`}>{u.role.replace("_", " ")}</span></td>
                  <td className="text-[#737A76] text-xs">{u.role === "super_admin" ? "All" : (u.park_ids || []).map(parkName).join(", ") || "—"}</td>
                  <td className="text-[#737A76]">{formatDate(u.created_at)}</td>
                  <td className="text-right pr-4">
                    <Button size="icon" variant="ghost" onClick={() => openEdit(u)}><Pencil size={14} /></Button>
                    <Button size="icon" variant="ghost" onClick={() => remove(u)} disabled={u.id === me?.id}><Trash2 size={14} className="text-[#A13E34]" /></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
